import { useQuery } from "@apollo/client";
import {
  EnforcementIntentsOrderBy,
  GET_ENFORCEMENT_INTENTS_SUMMARY,
  GetEnforcementIntentsSummaryData,
  GetEnforcementIntentsSummaryVars,
  GetEnforcementIntentsWhereFilter,
} from "common/graphql";
import {
  enforcementIntentSimpleStatus,
  EnforcementIntentSummary,
} from "common/graphql/fragments/enforcementIntentSummary";
import {
  parseFilterModelFromSearchParams,
  parseSortModel,
  filterModelToSearchParams,
  serializeSortModel,
} from "common/graphql/queryString";
import Paper from "@mui/material/Paper";
import {
  DataGridPro,
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
  GridFilterInputSingleSelect,
  GridFilterModel,
  GridSortModel,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import type { Theme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import { getViolationTypeLabel } from "common/format/violationType";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import { useApolloClient } from "@apollo/client";
import CustomExportButton from "@app.automotus.io/components/CustomExportButton";
import { utcToZonedTime } from "date-fns-tz";

export const NoticeSummaryTable: React.FC<NoticeSummaryTableProps> = ({ variant }) => {
  const navigate = useNavigate();
  const client = useApolloClient();

  const TABLE_COLUMNS = React.useMemo(
    () => [
      {
        field: "noticeNumber",
        headerName: "Notice Number",
        align: "left",
        headerAlign: "left",
        sortable: false,
        ...(variant === "all" ? { width: 160 } : { flex: 1 }),
      } as GridColDef<EnforcementIntentSummary, string>,
      {
        field: "violationType",
        headerName: "Violation Type",
        align: "left",
        headerAlign: "left",
        sortable: false,
        filterable: false,
        valueGetter({ row }) {
          return getViolationTypeLabel(row.violationType);
        },
        ...(variant === "all" ? { width: 162 } : { flex: 1 }),
      } as GridColDef<EnforcementIntentSummary, string>,
      {
        field: "licensePlate",
        headerName: "Plate",
        align: "left",
        headerAlign: "left",
        ...(variant === "all" ? { width: 107 } : { flex: 1 }),
        valueGetter({ row }) {
          return `${row.licensePlateRegion.split("-")[1].toUpperCase()} ${row.licensePlateNumber}`;
        },
        sortable: false,
        filterOperators: getGridStringOperators().filter((op) => ["equals", "contains"].includes(op.value)),
      } as GridColDef<EnforcementIntentSummary, string>,
      {
        field: "issuedAt",
        headerName: "Issue Date",
        type: "date",
        ...(variant === "all" ? { width: 143 } : { flex: 1 }),
        valueGetter({ row }) {
          return utcToZonedTime(row.issuedAt, row.timezone);
        },
        sortingOrder: ["desc", "asc"],
        filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
      } as GridColDef<EnforcementIntentSummary, Date>,
      ...(variant === "all"
        ? [
            {
              field: "status",
              headerName: "Status",
              align: "left",
              headerAlign: "left",
              type: "singleSelect",
              valueOptions: ["pending", "issued", "voided"],
              sortable: false,
              filterable: true,
              filterOperators: getGridStringOperators()
                .filter((op) => ["equals"].includes(op.value))
                .map((operator) => ({
                  ...operator,
                  InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
                })),
              valueGetter({ row }) {
                return enforcementIntentSimpleStatus[row.status];
              },
              valueFormatter({ value }) {
                return value.charAt(0).toUpperCase() + value.slice(1);
              },
              width: 80,
            } as GridColDef<EnforcementIntentSummary, string>,
            {
              field: "voidedReason",
              headerName: "Void Reason",
              align: "left",
              headerAlign: "left",
              sortable: false,
              filterable: false,
              valueGetter({ row }) {
                return row.reviews.find((r) => r.type === "officer")?.deniedReason || "N/A";
              },
              valueFormatter({ value }) {
                return value
                  .split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");
              },
              width: 254,
            } as GridColDef<EnforcementIntentSummary, string>,
          ]
        : []),
      {
        field: "actions",
        type: "actions",
        sortable: false,
        flex: 1,
        getActions: (params) =>
          variant === "pending"
            ? [
                <Button
                  onClick={() => navigate(`${params.row.id}`, { state: { from: "notice-summary" } })}
                  variant="text"
                  color="primary"
                  sx={{
                    textTransform: "none",
                  }}
                >
                  Review
                </Button>,
              ]
            : [
                <GridActionsCellItem
                  label={"View Citation"}
                  icon={<ArrowForwardIosIcon sx={{ color: (theme: Theme) => theme.palette.action.active }} />}
                  onClick={() => navigate(`${params.row.id}`, { state: { from: "notice-summary" } })}
                />,
              ],
      } as GridActionsColDef,
    ],
    [variant, navigate],
  );

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: PARAMS_BY_VARIANT[variant].defaultSort,
  });

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;

    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const PAGE_SIZE = 100;

  const sort = searchParams.get("sort") || "issuedAt+asc";
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };
  const orderBy: EnforcementIntentsOrderBy[] = React.useMemo(() => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "issuedAt":
        return [{ issued_at: sortDirection || "asc" }];
      default:
        return [{ issued_at: sortDirection || "asc" }];
    }
  }, [sortModel]);

  const getDefaultFilterByVariant = (variant: NoticeSummaryTableVariant): GetEnforcementIntentsWhereFilter => {
    return variant === "pending"
      ? {
          status: { _eq: "requires_officer_review" },
          reviews: { type: { _eq: "officer" }, reviewed_at: { _is_null: true } },
        }
      : {};
  };

  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      // Manually apply filters when user clears all filters
      handleApplyFilters(newFilterModel);
    }

    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  const filter = React.useMemo(() => {
    const baseFilter = getDefaultFilterByVariant(variant);

    if (!appliedFilters) {
      return baseFilter;
    }

    const columnFilters = appliedFilters.items
      .map((item) => {
        if (item.columnField === "noticeNumber") {
          return {
            notice_number: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
          };
        }
        if (item.columnField === "licensePlate") {
          return {
            license_plate_number: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
          };
        }
        if (item.columnField === "status" && item.operatorValue === "equals") {
          switch (item.value.toLowerCase()) {
            case "pending":
              return {
                status: { _eq: "requires_officer_review" },
                reviews: { type: { _eq: "officer" }, reviewed_at: { _is_null: true } },
              };
            case "issued":
              return { ro_lookup_submitted_at: { _is_null: false } };
            case "voided":
              return { voided_at: { _is_null: false }, voided_reason: { _neq: "internal_review" } };
            default:
              return {};
          }
        }
        if (item.columnField === "issuedAt") {
          const filterDate = new Date(`${item.value}T00:00:00`);
          const dateStart = startOfDay(filterDate);
          const dateEnd = endOfDay(filterDate);

          switch (item.operatorValue) {
            case "is":
              return { issued_at: { _gte: dateStart, _lte: dateEnd } };
            case "before":
              return { issued_at: { _lt: dateStart } };
            case "after":
              return { issued_at: { _gt: dateEnd } };
          }
        }

        return undefined;
      })
      .filter(Boolean);

    if (appliedFilters.linkOperator === "and") {
      return { ...baseFilter, _and: columnFilters };
    } else {
      return { ...baseFilter, _or: columnFilters };
    }
  }, [variant, appliedFilters]);

  const { data, loading, error } = useQuery<GetEnforcementIntentsSummaryData, GetEnforcementIntentsSummaryVars>(
    GET_ENFORCEMENT_INTENTS_SUMMARY,
    {
      variables: {
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        orderBy,
        filter,
      },
      fetchPolicy: "network-only",
    },
  );

  // Functions for custom export
  const fetchTotalCount = async () => {
    const result = await client.query<GetEnforcementIntentsSummaryData, GetEnforcementIntentsSummaryVars>({
      query: GET_ENFORCEMENT_INTENTS_SUMMARY,
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter,
      },
    });
    return result.data?.enforcementIntentAggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetEnforcementIntentsSummaryData, GetEnforcementIntentsSummaryVars>({
      query: GET_ENFORCEMENT_INTENTS_SUMMARY,
      variables: {
        offset,
        limit,
        orderBy,
        filter,
      },
    });
    return result.data?.enforcementIntents || [];
  };

  return (
    <Paper sx={{ borderRadius: 2, maxWidth: 1084, height: 876 }}>
      <DataGridPro
        components={{
          Toolbar: TableToolbar,
          FilterPanel: ManualApplyGridFilterPanel,
        }}
        componentsProps={{
          filterPanel: {
            onApplyFilters: handleApplyFilters,
          },
          toolbar: {
            fetchTotalCount,
            fetchDataBatch,
            exportFileName: `notices_${variant}_${Date.now()}`,
            columns: TABLE_COLUMNS,
          },
        }}
        columns={TABLE_COLUMNS}
        rows={data?.enforcementIntents || []}
        rowCount={data?.enforcementIntentAggregate.aggregate.count || 0}
        loading={loading}
        disableMultipleColumnsSorting
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        error={error}
        pagination
        page={page - 1}
        onPageChange={handlePageChange}
        pageSize={PAGE_SIZE}
        paginationMode={"server"}
        rowsPerPageOptions={[PAGE_SIZE]}
        sortingMode={"server"}
        sortModel={sortModel}
        filterMode={"server"}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        initialState={{
          sorting: {
            sortModel: [{ field: "issuedAt", sort: "asc" }],
          },
        }}
        onSortModelChange={handleSortModelChange}
        sx={{
          ".MuiDataGrid-columnHeaderTitle": {
            fontSize: "18px",
            fontWeight: 700,
            lineHeight: "24px",
            letterSpacing: 0.17,
          },
        }}
      />
    </Paper>
  );
};

export type NoticeSummaryTableVariant = "all" | "pending";

export interface NoticeSummaryTableProps {
  variant: NoticeSummaryTableVariant;
}

interface TableToolbarProps {
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  exportFileName: string;
  columns: GridColDef[];
}

const TableToolbar: React.FC<TableToolbarProps> = ({ fetchTotalCount, fetchDataBatch, exportFileName, columns }) => {
  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="center" height={52} px={1.25} spacing={1}>
      <GridToolbarFilterButton />
      <CustomExportButton
        getTotalCount={fetchTotalCount}
        fetchDataBatch={fetchDataBatch}
        fileName={exportFileName}
        columns={columns}
        sx={{
          fontSize: "16px",
          fontWeight: 400,
          letterSpacing: 0.15,
          textTransform: "none",
        }}
      />
    </Stack>
  );
};

const PARAMS_BY_VARIANT = {
  pending: {
    defaultSort: "issuedAt+asc",
  },
  all: {
    defaultSort: "issuedAt+desc",
  },
};

export default NoticeSummaryTable;
